import React from "react";
import { TrendingUp, TrendingDown, DollarSign, User, Package } from "lucide-react";
import Footer from "../Footer";
import Header from "../Header";
import EosBackdrop from "../EosBackdrop";
import { useUserDashboard } from "./useUserDashboard";

const UserDashboard: React.FC = () => {
  const {
    isAuthenticated,
    account,
    holdings,
    loading,
    error,
    refreshData,
    handleRefresh,
    formatCurrency,
    getPnLColor,
    getPnLDirection,
    getPnLPercentage,
    totalInvested,
    currentValue,
    totalProfit,
    totalProfitPercent,
  } = useUserDashboard();

  if (!isAuthenticated) {
    return (
      <div className="relative flex min-h-screen flex-col overflow-hidden text-slate-100">
        <EosBackdrop />
        <Header />
        <div className="relative z-10 flex-1 flex items-center justify-center text-center py-12 text-slate-400">
          Please log in to view your account details.
        </div>
        <Footer />
      </div>
    );
  }

  if (loading && !account) {
    return (
      <div className="relative flex min-h-screen flex-col overflow-hidden text-slate-100">
        <EosBackdrop />
        <Header />
        <div className="relative z-10 flex-1 flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent-400"></div>
          <span className="ml-3 text-slate-400">Loading account details...</span>
        </div>
        <Footer />
      </div>
    );
  }

  if (error) {
    return (
      <div className="relative flex min-h-screen flex-col overflow-hidden text-slate-100">
        <EosBackdrop />
        <Header />
        <div className="relative z-10 max-w-7xl mx-auto w-full flex-1 py-8 px-4 sm:px-6 lg:px-8">
          <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div className="text-red-300">{error}</div>
              <button
                onClick={() => refreshData(true)}
                className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
              >
                Retry
              </button>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="relative flex min-h-screen flex-col overflow-hidden text-slate-100">
      <EosBackdrop />
      <Header />
      <div className="relative z-10 max-w-7xl mx-auto w-full flex-1 space-y-8 py-8 px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        <div className="glass-card p-4 flex items-center">
          <div className="p-1.5 bg-accent-500/10 rounded-full">
            <User className="h-4 w-4 text-accent-300" />
          </div>
          <div className="ml-3 min-w-0 flex-1">
            <p className="text-xs font-medium text-slate-500 truncate">Account Name</p>
            <p className="text-sm font-semibold text-slate-100 truncate">
              {account?.profile?.user_name || account?.user_name || "N/A"}
            </p>
          </div>
        </div>
        <div className="glass-card p-4 flex items-center">
          <div className="p-1.5 bg-green-500/10 rounded-full">
            <DollarSign className="h-4 w-4 text-green-300" />
          </div>
          <div className="ml-3 min-w-0 flex-1">
            <p className="text-xs font-medium text-slate-500 truncate">Available Balance</p>
            <p className="text-sm font-semibold text-slate-100 truncate">
              {formatCurrency(account?.availableBalance?.cash || 0)}
            </p>
          </div>
        </div>

        <div className="glass-card p-4 flex items-center">
          <div className="p-1.5 bg-accent-500/10 rounded-full">
            <DollarSign className="h-4 w-4 text-accent-300" />
          </div>
          <div className="ml-3 min-w-0 flex-1">
            <p className="text-xs font-medium text-slate-500 truncate">Total Invested</p>
            <p className="text-sm font-semibold text-slate-100 truncate">{formatCurrency(totalInvested)}</p>
          </div>
        </div>
        <div className="glass-card p-4 flex items-center">
          <div className="p-1.5 bg-purple-500/10 rounded-full">
            <DollarSign className="h-4 w-4 text-purple-300" />
          </div>
          <div className="ml-3 min-w-0 flex-1">
            <p className="text-xs font-medium text-slate-500 truncate">Current Value</p>
            <p className="text-sm font-semibold text-slate-100 truncate">{formatCurrency(currentValue)}</p>
          </div>
        </div>
        <div className="glass-card p-4 flex items-center">
          <div className="p-1.5 bg-orange-500/10 rounded-full">
            <TrendingUp className="h-4 w-4 text-orange-300" />
          </div>
          <div className="ml-3 min-w-0 flex-1">
            <p className="text-xs font-medium text-slate-500 truncate">Total P&L</p>
            <p className="text-sm font-semibold text-slate-100 truncate">{formatCurrency(totalProfit)}</p>
            <p className={`text-xs font-medium ${getPnLColor(totalProfit)} truncate`}>
              {totalProfitPercent >= 0 ? "+" : ""}
              {totalProfitPercent.toFixed(2)}%
            </p>
          </div>
        </div>
      </div>

      <div className="glass-card">
        <div className="px-6 py-4 border-b border-white/10 flex justify-between items-center">
          <h3 className="text-lg font-medium text-slate-100">Portfolio Holdings (Total: {holdings.length})</h3>
          <button
            onClick={handleRefresh}
            disabled={loading}
            className="px-3 py-2 text-sm bg-accent-500 text-white rounded-lg hover:bg-accent-600 transition-colors disabled:opacity-50 flex items-center"
          >
            {loading ? (
              <span className="flex items-center">
                <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></span>
                Refreshing...
              </span>
            ) : (
              <>
                <span className="mr-2">↻</span>
                Refresh
              </>
            )}
          </button>
        </div>
        {holdings.length === 0 ? (
          <div className="px-6 py-12 text-center">
            <Package className="mx-auto h-12 w-12 text-slate-500" />
            <h3 className="mt-2 text-sm font-medium text-slate-100">No holdings</h3>
            <p className="mt-1 text-sm text-slate-400">You don't have any stocks in your portfolio yet.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-white/10">
              <thead className="bg-white/5">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Symbol
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Quantity
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Avg Price
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    LTP
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    P&L
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    P&L %
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/10">
                {holdings.map((h, idx) => (
                  <tr key={h.tradingsymbol || idx} className="hover:bg-white/5">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-slate-100">
                      {h.tradingsymbol}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-300">{h.quantity}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-300">
                      {formatCurrency(h.average_price)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-300">
                      {formatCurrency(h.last_price)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        {getPnLDirection(h.pnl) === "up" && <TrendingUp className="h-4 w-4 text-green-400" />}
                        {getPnLDirection(h.pnl) === "down" && <TrendingDown className="h-4 w-4 text-red-400" />}
                        <span className={`ml-1 text-sm font-medium ${getPnLColor(h.pnl)}`}>
                          {formatCurrency(h.pnl)}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`text-sm font-medium ${getPnLColor(h.pnl)}`}>
                        {getPnLPercentage(h.pnl, h.average_price, h.quantity)}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      </div>

      <Footer />
    </div>
  );
};

export default UserDashboard;
